import { accountKeys, type AccountClass, type AccountType, type Schemas } from '@taler/api-client'
import type { Locale } from '@taler/domain'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { computed, type ComputedRef } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useApi } from '@/app/api'
import { retrying } from '@/app/retrying'
import {
  flattenTree,
  listParentOptions,
  type AccountRow,
  type ParentOption,
} from '../internal/account-tree.ts'
import type { CreateAccountInput, UpdateAccountInput } from '../internal/account-form.ts'

export const ALL_CLASSES = 'ALL'
export const ALL_TYPES = 'ALL'

export interface UseAccountsTreeOptions {
  readonly ledgerId: () => string | undefined
  readonly locale: () => Locale
  /** The screen never issues a request for a capability the ledger did not grant. */
  readonly enabled: () => boolean
}

export interface ClassSummary {
  readonly root: AccountRow
  readonly accountCount: number
}

export interface UseAccountsTree {
  readonly rows: ComputedRef<readonly AccountRow[]>
  /** Every account, archived and hidden included, for a page that addresses one account by id. */
  readonly allRows: ComputedRef<readonly AccountRow[]>
  /** Each class root with its account count, over the whole chart - search and filters never narrow it. */
  readonly classSummaries: ComputedRef<readonly ClassSummary[]>
  readonly parentOptions: ComputedRef<readonly ParentOption[]>
  readonly isLoading: ComputedRef<boolean>
  readonly error: ComputedRef<Error | null>
  readonly retrying: ComputedRef<boolean>
  readonly showArchived: ComputedRef<boolean>
  readonly showHidden: ComputedRef<boolean>
  readonly search: ComputedRef<string>
  readonly classFilter: ComputedRef<AccountClass | typeof ALL_CLASSES>
  readonly typeFilter: ComputedRef<AccountType | typeof ALL_TYPES>
  readonly isCreating: ComputedRef<boolean>
  readonly isUpdating: ComputedRef<boolean>
  readonly isArchiving: ComputedRef<boolean>
  toggleShowArchived(): void
  toggleShowHidden(): void
  setSearch(value: string): void
  setClassFilter(value: AccountClass | typeof ALL_CLASSES): void
  setTypeFilter(value: AccountType | typeof ALL_TYPES): void
  retry(): void
  /** Resolves with the new account's id once the tree has refetched. */
  create(input: CreateAccountInput): Promise<string>
  update(input: UpdateAccountInput): Promise<void>
  archive(accountId: string): Promise<void>
  restore(accountId: string): Promise<void>
  hide(accountId: string): Promise<void>
  unhide(accountId: string): Promise<void>
}

const ARCHIVED_PARAM = 'archived'
const HIDDEN_PARAM = 'hidden'
const SEARCH_PARAM = 'q'
const CLASS_PARAM = 'class'
const TYPE_PARAM = 'type'

function firstValue(raw: unknown): string | undefined {
  const value = Array.isArray(raw) ? raw[0] : raw
  return typeof value === 'string' && value !== '' ? value : undefined
}

export function useAccountsTree(options: UseAccountsTreeOptions): UseAccountsTree {
  const api = useApi()
  const queryClient = useQueryClient()
  const route = useRoute()
  const router = useRouter()

  const active = computed(() => options.enabled() && options.ledgerId() !== undefined)
  const ledgerId = computed(() => options.ledgerId())

  const treeQuery = useQuery({
    queryKey: computed(() => accountKeys.tree(ledgerId.value ?? '')),
    queryFn: ({ signal }) => api.accounts.tree(ledgerId.value!, signal),
    enabled: active,
    retry: false,
  })

  const showArchived = computed(() => route.query[ARCHIVED_PARAM] === '1')
  const showHidden = computed(() => route.query[HIDDEN_PARAM] === '1')
  const search = computed(() => firstValue(route.query[SEARCH_PARAM]) ?? '')
  const classFilter = computed(
    () => (firstValue(route.query[CLASS_PARAM]) as AccountClass | undefined) ?? ALL_CLASSES,
  )
  const typeFilter = computed(
    () => (firstValue(route.query[TYPE_PARAM]) as AccountType | undefined) ?? ALL_TYPES,
  )

  function replaceQuery(patch: Record<string, string | undefined>): void {
    const next = { ...route.query, ...patch }
    for (const key of Object.keys(next)) {
      if (next[key] === undefined) delete next[key]
    }
    void router.replace({ query: next })
  }

  const nodes = computed(() => treeQuery.data.value ?? [])
  const allRows = computed(() =>
    flattenTree(nodes.value, {
      locale: options.locale(),
      showArchived: showArchived.value,
      showHidden: showHidden.value,
    }),
  )

  /** A class header stays iff the class filter allows it; the search and type filters only
   * ever narrow which accounts appear beneath it - see AccountStatementTable.vue. */
  const rows = computed(() => {
    const term = search.value.trim().toLocaleLowerCase()
    const klass = classFilter.value
    const type = typeFilter.value
    return allRows.value.filter((row) => {
      if (row.depth === 0) return klass === ALL_CLASSES || row.accountClass === klass
      if (klass !== ALL_CLASSES && row.accountClass !== klass) return false
      if (type !== ALL_TYPES && row.accountType !== type) return false
      if (term === '') return true
      return row.label.toLocaleLowerCase().includes(term) || row.code.toLocaleLowerCase().includes(term)
    })
  })

  const classSummaries = computed(() => {
    const out: { root: AccountRow; accountCount: number }[] = []
    for (const row of allRows.value) {
      if (row.depth === 0) out.push({ root: row, accountCount: 0 })
      else if (out.length > 0) out[out.length - 1]!.accountCount += 1
    }
    return out
  })

  function invalidate(): Promise<void> {
    const id = ledgerId.value
    if (id === undefined) return Promise.resolve()
    return Promise.all(
      accountKeys.invalidatedByWrite(id).map((queryKey) => queryClient.invalidateQueries({ queryKey })),
    ).then(() => undefined)
  }

  const createMutation = useMutation({
    mutationFn: (input: CreateAccountInput) => {
      const body: Schemas['CreateAccountRequest'] = {
        code: input.code,
        accountClass: input.accountClass,
        type: input.type,
        names: Object.fromEntries(Object.entries(input.names).map(([locale, name]) => [locale, { name }])),
        icon: input.icon,
        color: input.color,
        isGroup: input.isGroup,
        ...(input.currency === undefined ? {} : { currency: input.currency }),
        ...(input.parentId === undefined ? {} : { parentId: input.parentId }),
      }
      return api.accounts.create(ledgerId.value!, body).then((account) => account.id)
    },
    onSuccess: () => invalidate(),
  })

  const updateMutation = useMutation({
    mutationFn: (input: UpdateAccountInput) => {
      const body: Schemas['UpdateAccountRequest'] = {
        names: { en: { name: input.nameEn }, hu: { name: input.nameHu } },
        icon: input.icon,
        color: input.color,
      }
      return api.accounts.update(ledgerId.value!, input.id, body)
    },
    onSuccess: () => invalidate(),
  })

  const archiveMutation = useMutation({
    mutationFn: (accountId: string) => api.accounts.archive(ledgerId.value!, accountId),
    onSuccess: () => invalidate(),
  })

  const restoreMutation = useMutation({
    mutationFn: (accountId: string) => api.accounts.restore(ledgerId.value!, accountId),
    onSuccess: () => invalidate(),
  })

  const hideMutation = useMutation({
    mutationFn: (accountId: string) => api.accounts.hide(ledgerId.value!, accountId),
    onSuccess: () => invalidate(),
  })

  const unhideMutation = useMutation({
    mutationFn: (accountId: string) => api.accounts.unhide(ledgerId.value!, accountId),
    onSuccess: () => invalidate(),
  })

  return {
    rows,
    allRows: computed(() =>
      flattenTree(nodes.value, { locale: options.locale(), showArchived: true, showHidden: true }),
    ),
    classSummaries,
    parentOptions: computed(() => listParentOptions(nodes.value, options.locale())),
    isLoading: computed(() => active.value && treeQuery.isPending.value),
    error: computed(() => treeQuery.error.value),
    retrying: retrying(treeQuery),
    showArchived,
    showHidden,
    search,
    classFilter,
    typeFilter,
    isCreating: computed(() => createMutation.isPending.value),
    isUpdating: computed(() => updateMutation.isPending.value),
    isArchiving: computed(() => archiveMutation.isPending.value),
    toggleShowArchived: () => replaceQuery({ [ARCHIVED_PARAM]: showArchived.value ? undefined : '1' }),
    toggleShowHidden: () => replaceQuery({ [HIDDEN_PARAM]: showHidden.value ? undefined : '1' }),
    setSearch: (value) => replaceQuery({ [SEARCH_PARAM]: value.trim() === '' ? undefined : value }),
    setClassFilter: (value) => replaceQuery({ [CLASS_PARAM]: value === ALL_CLASSES ? undefined : value }),
    setTypeFilter: (value) => replaceQuery({ [TYPE_PARAM]: value === ALL_TYPES ? undefined : value }),
    retry: () => {
      void treeQuery.refetch()
    },
    create: (input) => createMutation.mutateAsync(input),
    update: (input) => updateMutation.mutateAsync(input).then(() => undefined),
    archive: (accountId) => archiveMutation.mutateAsync(accountId).then(() => undefined),
    restore: (accountId) => restoreMutation.mutateAsync(accountId).then(() => undefined),
    hide: (accountId) => hideMutation.mutateAsync(accountId).then(() => undefined),
    unhide: (accountId) => unhideMutation.mutateAsync(accountId).then(() => undefined),
  }
}
